<template>
  <div class="flex min-h-full flex-col">
    <!-- Header -->
    <header class="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
      <div class="mx-auto flex h-14 max-w-[1280px] items-center gap-3 px-3 md:px-6">
        <!-- Brand -->
        <router-link to="/" class="flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary-500 text-white shadow-sm">
            <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="5" r="2.2" /><circle cx="5" cy="18" r="2.2" /><circle cx="19" cy="18" r="2.2" />
              <path d="M12 7.2 6.6 16M12 7.2 17.4 16M6.8 18h10.4" />
            </svg>
          </span>
          <span class="leading-none">
            <span class="wh-display block text-[15px] font-bold tracking-tight text-gray-900 dark:text-slate-100">MQTT&nbsp;Proxy</span>
            <span class="block text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">Redirection</span>
          </span>
        </router-link>

        <!-- Nav (desktop) -->
        <nav class="ml-4 hidden items-center gap-0.5 md:flex">
          <router-link
            v-for="t in tabs" :key="t.to" :to="t.to"
            class="relative px-3.5 py-2 text-[13px] font-medium transition-colors"
            :class="isActive(t) ? 'text-gray-900 dark:text-slate-50' : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'">
            {{ t.label }}
            <span v-if="isActive(t)" class="absolute inset-x-3 -bottom-[11px] h-0.5 rounded-full" :style="{ backgroundColor: t.color }"></span>
          </router-link>
        </nav>

        <div class="ml-auto flex items-center gap-1.5">
          <!-- Toggle thème -->
          <button class="wh-icon-btn" :title="isDark ? 'Mode clair' : 'Mode sombre'" @click="toggleTheme">
            <svg v-if="isDark" viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
            <svg v-else viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
            </svg>
          </button>

          <!-- Utilisateur -->
          <div class="flex items-center gap-2 pl-1">
            <span class="hidden text-[13px] font-medium text-gray-600 dark:text-slate-300 sm:block">{{ username }}</span>
            <span class="grid h-8 w-8 place-items-center rounded-full bg-secondary-500 text-[12px] font-bold uppercase text-white">{{ initials }}</span>
            <button class="wh-icon-btn" title="Se déconnecter" @click="logout">
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Nav (mobile) -->
      <nav class="flex items-center gap-1 border-t border-gray-100 px-3 py-1.5 md:hidden dark:border-slate-700/60">
        <router-link
          v-for="t in tabs" :key="'m-' + t.to" :to="t.to"
          class="rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors"
          :class="isActive(t) ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-slate-50' : 'text-gray-500 dark:text-slate-400'">
          {{ t.label }}
        </router-link>
      </nav>
    </header>

    <!-- Contenu -->
    <main class="mx-auto w-full max-w-[1280px] flex-1 px-3 py-5 md:px-6 md:py-7">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </main>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isDark: document.documentElement.classList.contains('dark'),
      tabs: [
        { to: '/', label: 'Tableau de bord', color: '#23D2CD', match: ['/'] },
        { to: '/devices', label: 'Devices', color: '#ED107D', match: ['/devices', '/device'] },
      ],
    }
  },
  computed: {
    username() {
      return this.$keycloak?.tokenParsed?.preferred_username || this.$keycloak?.tokenParsed?.name || 'Utilisateur'
    },
    initials() {
      const n = this.username || '?'
      return n.slice(0, 2).toUpperCase()
    },
  },
  methods: {
    isActive(tab) {
      const p = this.$route.path
      if (tab.to === '/') return p === '/'
      return tab.match.some(m => p === m || p.startsWith(m + '/') || p === m)
    },
    toggleTheme() {
      this.isDark = !this.isDark
      const root = document.documentElement
      root.classList.toggle('dark', this.isDark)
      try { localStorage.setItem('wh-theme', this.isDark ? 'dark' : 'light') } catch (e) { /* noop */ }
    },
    logout() {
      if (this.$keycloak) this.$keycloak.logout()
    },
  },
}
</script>
